import { flow, map, toPairs, compact, isEmpty, fromPairs, concat } from 'lodash'
import { getValidation } from './validations.js';
import { getFieldName, inspect, convertFormObjects } from '../formHelper.js';

// -------------------- Validation Utils --------------------
const getCustomValidations = (validations) =>
  flow([
    toPairs,
    validatePair => map(validatePair, ([vName, vValue]) => getValidation(vName, vValue))
  ])(validations);

// Format/filter validations here,
// eg) multiple_choice.toggle does not expect to receive min_count nor max_count
// so filter them out here
const normalizeValidations = (object) => {
  const inspected = inspect(object);

  // Make sure to remove the min_count/max_count validations from multiple toggle,
  // otherwise the validations would go wrong, like trying to count the length of
  // string value
  if (inspected.multipleToggle) {
    const newValidations = { ...object.validations };
    delete newValidations.min_count;
    delete newValidations.max_count;
    return newValidations;
  }

  return object.validations;
};

const validationExecutor = (validationSet, value) =>
  flow([
    validationSet => map(validationSet, f => f(value)),
    compact,
    errors => errors.length > 0 ? errors : undefined
  ])(validationSet);

// -------------------- Main Functions --------------------
export const getValidationSets = objects => {
  const validationSets = flow([
    convertFormObjects,
    obj => map(obj, object => {
      const { validations, purpose } = object;
      const name = getFieldName(object);

      const normalizedValidations = normalizeValidations(object)
      const customValidations = getCustomValidations(normalizedValidations);
      const purposeValidations = [getValidation(purpose, purpose === 'date' ? normalizedValidations : undefined)];
      const validationData = {
        validationSet: concat(customValidations, purposeValidations),
        isRequired: validations.required
      };

      return [name, validationData];
    }),
    fromPairs
  ])(objects);

  return validationSets;
};

export const validator = validationSets => values => {
  // If values is empty, should return it as it is,
  // This is required since redux-form would run this function
  // even without initializing
  if (isEmpty(values)) return values;

  const errors = flow([
    toPairs,
    pairs => map(pairs, ([name, { validationSet, isRequired }]) => {
      const value = values[name];
      const errors = !isRequired && !value
        ? undefined
        : validationExecutor(validationSet, value);
      return [name, errors];
    }),
    fromPairs
  ])(validationSets);

  return errors;
};
